import { useState, useEffect } from 'react';
import { X, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthContext } from '@/contexts/AuthContext';
import { useYears } from '@/hooks/useYears';
import { supabase } from '@/integrations/supabase/client';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';

const DISMISS_KEY = 'kalm_setyear_dismissed';

export function SetYearBanner() {
  const { user, profile, isStudent, patchProfile, refreshProfile } = useAuthContext();
  const { data: years, isLoading: yearsLoading } = useYears();
  const [chosenYearId, setChosenYearId] = useState('');
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Read session-only dismiss flag on mount
  useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') {
        setDismissed(true);
      }
    } catch {
      // sessionStorage may be unavailable (private mode); ignore
    }
  }, []);

  // Self-gate: render nothing unless a signed-in student with no preferred year,
  // a loaded profile, and loaded years. Also hide if dismissed this session.
  if (
    !isStudent ||
    !profile ||
    profile.preferred_year_id ||
    yearsLoading ||
    !years ||
    dismissed
  ) {
    return null;
  }

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // ignore
    }
    setDismissed(true);
  };

  const handleSave = async () => {
    if (!chosenYearId || !user) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ preferred_year_id: chosenYearId })
        .eq('id', user.id);

      if (error) throw error;

      // Optimistic UI update + server refresh
      patchProfile({ preferred_year_id: chosenYearId });
      refreshProfile();
      toast.success('Study year saved');
    } catch (error) {
      console.error('Failed to save study year:', error);
      toast.error('Could not save your year — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative flex items-center gap-3 px-4 py-3 rounded-lg border border-primary/20 bg-primary/5 mb-4 animate-fade-in">
      <GraduationCap className="w-5 h-5 text-primary flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">
          Choose your study year
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Pick your year to unlock your modules and download your books.
        </p>
      </div>

      <Select value={chosenYearId} onValueChange={setChosenYearId}>
        <SelectTrigger className="h-8 w-[140px] bg-background text-xs">
          <SelectValue placeholder="Select year" />
        </SelectTrigger>
        <SelectContent>
          {years.map((year) => (
            <SelectItem key={year.id} value={year.id}>
              {year.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        size="sm"
        className="h-8 text-xs"
        disabled={!chosenYearId || saving}
        onClick={handleSave}
      >
        {saving ? 'Saving…' : 'Save'}
      </Button>

      <button
        onClick={handleDismiss}
        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
